import { Controller, useForm } from "react-hook-form";
import {
  Dispatch,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { Column, Row, Spacer } from "../../../../components/Layout/Layout";
import {
  decodeContactDetails,
  encodeContactDetails,
} from "../../../../core/api/contacts/helpers";
import { ContactDetails } from "../../../../core/api/contacts/types";
import { ScheduleItem } from "../../../../ui-lib/components/Scheduler/ScheduleItem";
import useTranslations from "../../../../core/i18n/useTranslations";
import Alert from "../../../../ui-lib/components/Alerts/Alert";
import Button from "../../../../ui-lib/components/Button/Button";
import Checkbox from "../../../../ui-lib/components/Checkbox/Checkbox";
import Dropdown from "../../../../ui-lib/components/Dropdown/Dropdown";
import TextInput from "../../../../ui-lib/components/Inputs/TextInput";
import RadioGroup from "../../../../ui-lib/components/Radio/RadioGroup";
import {
  useObjectForm,
  useObjectFormRef,
} from "../../../../core/SaveObjectContext/hooks";
import Icon from "../../../../ui-lib/components/Icon";
import Divider from "../../../../ui-lib/components/Divider/Divider";
import useUser from "../../../../core/user/useUser";
import useSharedContact from "../../../CommonContacts/SharedContactContext/useSharedContact";
import ContactInfoForm from "./ContactInfoForm";
import TimePicker from "../../../../ui-lib/components/DateAndTime/TimePicker";
import useLanguage from "../../../../core/i18n/useLanguage";
import TextArea from "../../../../ui-lib/components/Inputs/Textarea";
import { notifyApiErrors } from "../../../../core/helpers/helpers";
import PrimeModal from "../../../../ui-lib/components/PrimeModal/PrimeModal";
import { CustomDropdown } from "../../../../ui-lib/components/Hierarchy/CustomTreeDropdown/CustomDropdown";
import { isOrganizationVisible } from "../../../../ui-lib/utils/organizationsHelper";
import GooglePlaceAutocomplete from "../../../../components/GooglePlaceAutocomplete";

const displayInCentralOptions = [0, 1, 2];

const callMethodOptions = [99, 0, 1];

export declare type ContactFormActions = {
  submitForm: any;
};

export interface ContactFormProps {
  onSubmit: (
    values: ContactDetails,
    reset?: (values: ContactDetails) => void,
    valuesForReset?: ContactDetails
  ) => Promise<void>;
  defaultValues?: ContactDetails;
  setCreateLogin?: Dispatch<boolean>;
  isModal?: boolean;
  hideTime?: boolean;
  withNewSchedule?: boolean;
  privateContact?: boolean;
}

export const ContactForm = forwardRef<ContactFormActions, ContactFormProps>(
  (
    {
      defaultValues,
      onSubmit,
      setCreateLogin,
      isModal = true,
      hideTime = false,
      withNewSchedule = false,
      privateContact = false,
    }: ContactFormProps,
    ref
  ) => {
    const t = useTranslations();
    const user = useUser();
    const { currentLanguage } = useLanguage();
    const isOrgVisible = isOrganizationVisible(user);

    const {
      setNewData,
      setShowChangeContactModal,
      updateContact,
      showChangeContactModal,
      newData,
      isLoading,
    } = useSharedContact();

    const hasTrygghetsjourenJourledRole = useMemo(
      () => user.config?.show.includes("ShowObjectDetailsVariant1"),
      []
    );

    const contactTypeOptions = [
      {
        id: "contact_person",
        isResource: false,
      },
      ...(!hasTrygghetsjourenJourledRole
        ? [
            {
              id: "resource",
              isResource: true,
            },
          ]
        : []),
    ];

    const {
      control,
      formState: { errors },
      watch,
      setValue,
      getValues,
      handleSubmit,
      trigger,
      reset,
      clearErrors,
    } = (isModal ? useForm : useObjectForm)<ContactDetails>({
      mode: "onChange",
      defaultValues: defaultValues
        ? encodeContactDetails(defaultValues)
        : {
            globalType: 0,
            isResource: false,
            sendResponseAlarm: false,
            hideFromResponseCenter: false,
            callMethod: 99,
          },
    });
    const formRef = useObjectFormRef();
    const isResource = watch("isResource");
    const callMethod = watch("callMethod");
    const [showNewScheduler, setShowNewScheduler] = useState(false);

    const internalSubmit = async (): Promise<any> => {
      try {
        handleSubmit(async (values: ContactDetails) => {
          const decoded = decodeContactDetails(values);

          // Global type is not relevant for contacts of type resource
          if (decoded.isResource) {
            decoded.globalType = 0;
          }
          setNewData(decoded);
          await onSubmit(decoded);
          reset(values);
        })();
      } catch (error: any) {
        notifyApiErrors(error.response?.data?.errors);
      }
    };

    useImperativeHandle<ContactFormActions, ContactFormActions>(ref, () => ({
      submitForm: internalSubmit,
    }));

    const renderGlobalType = () => (
      <Controller
        name="globalType"
        control={control}
        render={({ field }) => {
          const dropdownItems = displayInCentralOptions.map((id) => ({
            id,
            name: t(`Contacts:input_display_in_central_${id}`),
            isSelected: isResource ? id === 0 : id === field.value,
          }));

          if (isResource || privateContact) {
            return <></>;
          }

          return (
            <Dropdown
              title={t("Contacts:input_display_in_central_label")}
              selectedItem={dropdownItems.find((i) => i.isSelected)}
              items={dropdownItems}
              onSelectItem={(item) => setValue(field.name, item.id as number)}
            />
          );
        }}
      />
    );

    const renderDescription = () => (
      <Controller
        name="description"
        control={control}
        render={({ field }) => (
          <TextInput
            label={
              privateContact
                ? t("Objects:labels_contact_relation")
                : t("Objects:general_relation")
            }
            placeholder={t("Objects:placeholder_contact_relation")}
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />
    );

    return (
      <>
        <PrimeModal
          withFooter
          withHeader
          fixedWidth="576px"
          header={t("Contacts:change_contact_resource")}
          isOpen={showChangeContactModal}
          loading={isLoading}
          submitBtn={{
            text: t("Contacts:yes_continue"),
            variant: "destructive",
            onClick: () => {
              updateContact(newData as ContactDetails);
            },
          }}
          cancelBtn={{
            text: t("Common:cancel"),
            onClick: () => {
              setShowChangeContactModal(false);
            },
          }}
          onClose={() => {
            setShowChangeContactModal(false);
            reset(defaultValues);
          }}
        >
          <p>
            {!newData?.isResource
              ? t("Contacts:change_contact_resource_type_description").replace(
                  "{}",
                  defaultValues?.email!
                )
              : t(
                  "Contacts:change_contact_resource_call_method_description"
                ).replace("{}", defaultValues?.email!)}
          </p>
        </PrimeModal>
        <form
          ref={formRef}
          id="contact_form"
          className="w-100"
          onSubmit={!isModal ? internalSubmit : undefined}
        >
          <Controller
            name="isResource"
            control={control}
            render={({ field }) => {
              const radioGroupItems = contactTypeOptions.map((o, i) => ({
                id: i,
                value: o.id,
                text: t(`Contacts:input_${o.id}`),
                description:
                  o.id !== "contact_person" || !hasTrygghetsjourenJourledRole
                    ? t(`Contacts:input_${o.id}_description`)
                    : "",
              }));

              return (
                <RadioGroup
                  label={t("Contacts:input_contact_type_label")}
                  name={field.name}
                  items={radioGroupItems}
                  selectedValue={
                    contactTypeOptions.find((o) => o.isResource === field.value)
                      ?.id
                  }
                  onChange={(value) => {
                    const isResorce = !!contactTypeOptions.find(
                      (o) => o.id === value
                    )?.isResource;

                    setValue(field.name, isResorce);
                  }}
                />
              );
            }}
          />

          <Spacer size={16} />

          <Row align="start">
            <Column>
              <Controller
                name="name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextInput
                    required
                    inputRef={field.ref}
                    label={t("Contacts:contact_name")}
                    placeholder={t("Common:placeholder_name")}
                    value={field.value}
                    onChange={field.onChange}
                    validationError={
                      errors.name && t("Errors:input_field_required")
                    }
                  />
                )}
              />
            </Column>

            <Spacer size={32} />

            <Column>
              {isOrgVisible && !privateContact ? (
                <Controller
                  name="organizationId"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <CustomDropdown
                      required
                      selectionMode="single"
                      fullWidth
                      label={t("Common:belongs_to")}
                      placeholder={t("Common:organisation")}
                      customerFilter={{
                        customers: field.value?.toString(),
                      }}
                      onSelectSignle={(data) => {
                        const organizationId = data?.key as number | undefined;
                        setValue(field.name, organizationId);
                        trigger(field.name);
                      }}
                      validationError={
                        errors.organizationId &&
                        t("Errors:input_field_required")
                      }
                    />
                  )}
                />
              ) : (
                renderDescription()
              )}
            </Column>
          </Row>

          <Spacer size={16} />

          {(!isResource || isOrgVisible) &&
            !privateContact &&
            !defaultValues?.userId &&
            !hasTrygghetsjourenJourledRole && (
              <>
                <Row align="start">
                  <Column align="start">
                    {isOrgVisible && renderDescription()}
                  </Column>
                  <Spacer size={32} />
                  <Column>{renderGlobalType()}</Column>
                </Row>
                <Spacer size={16} />
              </>
            )}

          {!isResource &&
            defaultValues?.userId &&
            !hasTrygghetsjourenJourledRole && (
              <>
                <Row align="start">
                  <Column align="start">{renderGlobalType()}</Column>
                </Row>
                <Spacer size={16} />
              </>
            )}

          {isResource && (
            <>
              <Row align="center">
                <Column className="w-100">
                  <Controller
                    name="callMethod"
                    control={control}
                    render={({ field }) => {
                      const dropdownItems = callMethodOptions.map((id) => ({
                        id,
                        name: t(`Contacts:input_call_method_${id}`),
                        isSelected: id === field.value,
                      }));

                      return (
                        <Dropdown
                          title={t("Contacts:input_call_method")}
                          width="100%"
                          selectedItem={dropdownItems.find((i) => i.isSelected)}
                          items={dropdownItems}
                          onSelectItem={(item) => {
                            setCreateLogin?.(
                              item.id === 1 && !defaultValues?.adminId
                            );
                            setValue(field.name, item.id as number);
                          }}
                        />
                      );
                    }}
                  />
                </Column>
                <Spacer size={32} />
                <Column className="w-100">
                  {callMethod === 1 && setCreateLogin && (
                    <>
                      <Spacer size={23} />
                      <Checkbox
                        label={t("Contacts:create_logn")}
                        checked
                        disabled
                      />
                    </>
                  )}
                </Column>
              </Row>

              {defaultValues?.adminId && callMethod === 1 && (
                <>
                  <Spacer size={16} />
                  <Row type="left" align="center">
                    <Link
                      to={`/adminportal/administrators/${defaultValues?.adminId}`}
                      className="fw-600"
                    >
                      {t("Contacts:edit_linked_resource")}
                    </Link>
                    <Icon
                      name="chevron-right"
                      size={14}
                      color="Primary-700"
                      margin="3px 0 0"
                    />
                  </Row>
                  <Spacer size={16} />
                  <Divider />
                </>
              )}

              <Spacer size={16} />
            </>
          )}

          {getValues("callMethod") !== 1 && (
            <>
              <Alert
                title={t("Errors:input_contacts_required_fields")}
                variant="info"
                withDefaultIcon
              />

              <Spacer size={16} />
            </>
          )}

          <ContactInfoForm
            control={control}
            errors={errors}
            hasTrygghetsjourenJourledRole={hasTrygghetsjourenJourledRole}
            watch={watch}
            getValues={getValues}
            setValue={setValue}
            trigger={trigger}
          />

          {!hasTrygghetsjourenJourledRole && (
            <>
              <Spacer size={16} />

              <Row>
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <GooglePlaceAutocomplete
                      label={t("Common:label_address")}
                      placeholder={t("Common:placeholder_address")}
                      value={field.value}
                      onChange={field.onChange}
                      onValueChange={(val) => setValue("address", val)}
                      onPlaceSelected={({
                        formattedAddress,
                        latitude,
                        longitude,
                      }) => {
                        setValue(
                          "address",
                          formattedAddress ?? field.value ?? ""
                        );
                        setValue("latitude", latitude);
                        setValue("longitude", longitude);
                      }}
                      apiKey={user.googleKey?.api_key}
                      language={
                        currentLanguage?.nativeName ??
                        user.config?.primaryLanguage
                      }
                    />
                  )}
                />
                <Spacer size={32} />
                <Controller
                  name="billingReference"
                  control={control}
                  rules={{ maxLength: 45 }}
                  render={({ field }) => (
                    <TextInput
                      inputRef={field.ref}
                      label={t("Common:billing_reference")}
                      placeholder={t("Common:billing_placeholder")}
                      value={field.value}
                      onChange={field.onChange}
                      validationError={
                        errors?.billingReference &&
                        t("Errors:max_chars_exceeded").replace("{0}", "45")
                      }
                    />
                  )}
                />
              </Row>
              <Spacer size={16} />

              <Row align="start">
                <Controller
                  name="notes"
                  control={control}
                  rules={{ maxLength: 400 }}
                  render={({ field }) => (
                    <TextArea
                      inputRef={field.ref}
                      label={t("Common:labels_additional_info")}
                      value={field.value}
                      onChange={field.onChange}
                      validationError={
                        errors?.notes &&
                        t("Errors:max_chars_exceeded").replace("{0}", "400")
                      }
                    />
                  )}
                />
              </Row>

              {withNewSchedule && (
                <>
                  <Spacer size={16} />

                  {showNewScheduler ? (
                    <>
                      <h2>{t("Menu:availability")}</h2>
                      <Spacer size={8} />
                      <p>{t("Contacts:availability_description")}</p>
                      <Spacer size={16} />
                      <ScheduleItem
                        dateFormat={user.dateFormat}
                        skipViewMode
                        partOfForm
                        control={control}
                        errors={errors}
                        watch={watch}
                        setValue={setValue}
                        getValues={getValues}
                        trigger={trigger}
                        onItemRemove={() => {
                          setShowNewScheduler(false);
                          setValue("scheduleItem", undefined);
                        }}
                        clearErrors={clearErrors}
                      />
                      <Spacer size={16} />
                      <Alert
                        title={t("Contacts:more_on_edit")}
                        variant="info"
                        icon="information-circle"
                      />
                    </>
                  ) : (
                    <Button
                      image="plus"
                      variant="border-link"
                      text={t("Contacts:set_availability")}
                      onClick={() => {
                        setShowNewScheduler(true);
                        setValue(
                          "scheduleItem.repeatDays",
                          [0, 1, 2, 3, 4, 5, 6]
                        );
                        setValue("scheduleItem.repeatType", "RepeatDays");
                      }}
                    />
                  )}
                </>
              )}

              {!(hideTime || withNewSchedule) && (
                <>
                  <Spacer size={16} />

                  <Row align="start">
                    <Controller
                      name="startTime"
                      control={control}
                      render={({ field }) => (
                        <TimePicker
                          label={t("Contacts:input_start_time")}
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                    <Spacer size={32} />
                    <Controller
                      name="endTime"
                      control={control}
                      render={({ field }) => (
                        <TimePicker
                          label={t("Contacts:input_end_time")}
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </Row>
                </>
              )}
              <Spacer size={16} />

              <Row align="start">
                <Controller
                  name="sendResponseAlarm"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      label={t("Contacts:input_send_info")}
                      checked={field.value}
                      onChange={field.onChange}
                      style={{ padding: 0, width: "100%" }}
                    />
                  )}
                />
                <Spacer size={32} />
                <Controller
                  name="hideFromResponseCenter"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      label={t("Contacts:input_hide")}
                      checked={field.value}
                      onChange={field.onChange}
                      style={{ padding: 0, width: "100%" }}
                    />
                  )}
                />
              </Row>
            </>
          )}
        </form>
      </>
    );
  }
);
